import { ChangeEvent } from "react";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import TextField from "@components/FormsV2/TextField/TextField";
import EditableDataTable from "@components/EditableDataTable";
import type { Column } from "@components/EditableDataTable/types/column";
import {
  BatchActionPill,
  useBatchSelection,
  type BatchAction,
} from "@components/CatalogIndex";
import { newServiceCategoryRow } from "../../types";
import type { ServiceCategoryRow } from "../../types";
import "./ServiceCategoriesEditor.css";

export interface ServiceCategoriesEditorProps {
  rows: ServiceCategoryRow[];
  onChangeRows: (rows: ServiceCategoryRow[]) => void;
}

type CategoryRecord = {
  id: string; // clientKey (drag + selection id)
  position: number;
  row: ServiceCategoryRow;
};

export const ServiceCategoriesEditor = ({
  rows,
  onChangeRows,
}: ServiceCategoriesEditorProps): JSX.Element => {
  const live = rows.filter((r) => !r._deleted);
  const deleted = rows.filter((r) => r._deleted);
  const sel = useBatchSelection([]);

  const updateRow = (clientKey: string, patch: Partial<ServiceCategoryRow>) =>
    onChangeRows(
      rows.map((r) => (r.clientKey === clientKey ? { ...r, ...patch } : r))
    );

  const addRow = () => onChangeRows([...rows, newServiceCategoryRow()]);

  // Deferred multi-select delete (our standard react-table delete pattern):
  // saved rows are flagged `_deleted` (applied on Save), unsaved rows are
  // dropped outright. Selection is by clientKey.
  const handleBatchDelete = () => {
    const selected = new Set(sel.selectedIds);
    const next = rows
      .filter((r) => !(selected.has(r.clientKey) && !r.id))
      .map((r) =>
        selected.has(r.clientKey) && r.id ? { ...r, _deleted: true } : r
      );
    onChangeRows(next);
    sel.clear();
  };

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: handleBatchDelete,
      variant: "danger",
    },
  ];

  // EditableDataTable hands back records sorted by their new position; map them
  // back to live rows by clientKey, then re-append the deleted rows (kept for
  // the save-time diff).
  const handleReorder = (updated: CategoryRecord[]) => {
    const reordered = updated
      .map((rec) => live.find((r) => r.clientKey === rec.id))
      .filter((r): r is ServiceCategoryRow => Boolean(r));
    onChangeRows([...reordered, ...deleted]);
  };

  const records: CategoryRecord[] = live.map((row, i) => ({
    id: row.clientKey,
    position: i + 1,
    row,
  }));

  const columns: Column[] = [
    {
      id: "name",
      header: () => "Name",
      cell: (rec: CategoryRecord) => (
        <TextField
          label={null}
          id={`category-name-${rec.row.clientKey}`}
          value={rec.row.name}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateRow(rec.row.clientKey, { name: e.target.value })
          }
        />
      ),
    },
    {
      id: "code",
      header: () => "Code",
      cell: (rec: CategoryRecord) => (
        <TextField
          label={null}
          id={`category-code-${rec.row.clientKey}`}
          value={rec.row.code}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            updateRow(rec.row.clientKey, { code: e.target.value })
          }
        />
      ),
    },
  ];

  return (
    <>
      <div className="serviceCategoriesHeader">
        <span className="adminFormSectionTitle">Service Categories</span>
        <BatchActionPill
          selectedCount={sel.selectedIds.length}
          actions={batchActions}
        />
      </div>
      {live.length === 0 ? (
        <p className="text-muted">No service categories yet.</p>
      ) : (
        <EditableDataTable
          records={records}
          columns={columns}
          config={{
            classForTable: "serviceCategoriesTable",
            multiselect: true,
            dragAndDrop: live.length > 1,
          }}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          setData={handleReorder}
        />
      )}
      <button type="button" className="serviceCategoryAddLink" onClick={addRow}>
        + Add Service Category
      </button>
    </>
  );
};

export default ServiceCategoriesEditor;
